'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppDownloadModal from '@/components/AppDownloadModal';
import { SITE_NAME } from '@/lib/site-config';

const sections = [
  {
    id: 'overview',
    title: '1. Overview',
    content: `This Refund Policy applies to all payments made on the ${SITE_NAME} platform by brands, sellers, and reviewers. Please read this policy carefully before making any payment. By completing a payment on our platform, you agree to this Refund Policy.`,
  },
  {
    id: 'brand-refunds',
    title: '2. Brand / Seller Refunds',
    content: `Campaign fees paid by brands are generally non-refundable once a campaign has been published and made available to reviewers. However, refunds may be considered in the following circumstances: (a) Technical errors resulting in duplicate charges — full refund within 7 business days; (b) Campaign not published within 48 hours of payment due to platform error — full refund; (c) Cancellation request submitted within 2 hours of payment and before any reviewer applications — full refund minus a 5% processing fee. Partial refunds may be issued for unused campaign slots at our discretion.`,
  },
  {
    id: 'reviewer-earnings',
    title: '3. Reviewer Earnings & Withdrawals',
    content: `Reviewer earnings credited to your wallet are not subject to refund to the platform. Once a withdrawal request is processed and funds are transferred to your UPI or bank account, the transaction is final and cannot be reversed. If you believe a payout was incorrect, contact support@indianreviewclub.com within 7 days of the transaction.`,
  },
  {
    id: 'disputed-reviews',
    title: '4. Disputed or Rejected Reviews',
    content: `If a review is rejected by our quality team for policy violations, the associated earnings will not be credited. If you believe a review was incorrectly rejected, you may raise a dispute within 14 days. We will review your appeal and respond within 5 business days. Earnings from fraudulent or policy-violating reviews are permanently forfeited.`,
  },
  {
    id: 'process',
    title: '5. Refund Process',
    content: `To request a refund, email billing@indianreviewclub.com with your registered email, transaction ID, amount, and reason for the refund request. We will acknowledge your request within 24 hours and process eligible refunds within 7–10 business days. Refunds are credited to the original payment method. We do not issue refunds via cash or cheque.`,
  },
  {
    id: 'non-refundable',
    title: '6. Non-Refundable Items',
    content: `The following are strictly non-refundable: (a) Campaign fees after reviewer applications have been received; (b) Platform subscription fees (if applicable); (c) Fees for completed campaigns; (d) Processing fees charged by payment gateways; (e) Earnings forfeited due to policy violations.`,
  },
  {
    id: 'chargebacks',
    title: '7. Chargebacks',
    content: `If you initiate a chargeback with your bank or payment provider without first contacting us, we reserve the right to suspend your account pending investigation. Fraudulent chargebacks may result in permanent account termination and recovery of funds through legal means. We encourage all users to contact our support team first to resolve payment disputes.`,
  },
  {
    id: 'contact',
    title: '8. Contact for Refunds',
    content: `For all refund-related queries, contact us at billing@indianreviewclub.com. Please include your account email, transaction details, and a brief description of your issue. Our billing team operates Monday–Friday, 10 AM–6 PM IST.`,
  },
];

export default function RefundPage() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Header onDownloadClick={() => setShowModal(true)} />
      <main className="min-h-screen bg-white pt-16">
        <section className="bg-[#0F0F1A] text-white py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-white/70">Legal</span>
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl mb-4 leading-tight">
              Refund Policy
            </h1>
            <p className="text-white/50 text-base max-w-2xl leading-relaxed">
              Understand our refund and cancellation policies for brands, sellers, and reviewers on the platform.
            </p>
            <p className="text-white/30 text-sm mt-4">Last updated: August 2026</p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-56 shrink-0">
              <div className="sticky top-24">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Contents</p>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#${s?.id}`}
                      className="block text-sm text-gray-500 hover:text-primary transition-colors py-1 border-l-2 border-transparent hover:border-primary pl-3"
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <article className="flex-1 min-w-0 space-y-10">
              {sections?.map((s) => (
                <div key={s?.id} id={s?.id} className="scroll-mt-24">
                  <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">{s?.title}</h2>
                  <p className="text-gray-600 leading-relaxed text-[15px]">{s?.content}</p>
                </div>
              ))}

              <div className="bg-orange-50 border border-orange-100 rounded-2xl p-6 mt-8">
                <p className="text-sm font-semibold text-gray-800 mb-1">Billing questions?</p>
                <p className="text-sm text-gray-500">
                  Email us at{' '}
                  <a href="mailto:billing@indianreviewclub.com" className="text-primary hover:underline">
                    billing@indianreviewclub.com
                  </a>{' '}
                  — we respond within 24 hours on business days.
                </p>
              </div>
            </article>
          </div>
        </div>
      </main>
      <Footer />
      {showModal && <AppDownloadModal onClose={() => setShowModal(false)} />}
    </>
  );
}
